import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { MdOutlineSettings, MdClose } from "react-icons/md";
import { FaPaperPlane } from "react-icons/fa";

export const StatusBadge = ({ status }) => {
  const getStyles = (s) => {
    const normalize = s.toLowerCase();
    if (normalize.includes("process") || normalize.includes("inward"))
      return "bg-amber-100 text-amber-800 border-amber-200";
    if (normalize.includes("available"))
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (normalize.includes("memo"))
      return "bg-sky-100 text-sky-800 border-sky-200";
    if (normalize.includes("sold"))
      return "bg-gray-100 text-gray-700 border-gray-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${getStyles(
        status
      )}`}
    >
      {status}
    </span>
  );
};

export const Button = ({
  children,
  variant = "primary",
  icon: Icon,
  size = "md",
  className = "",
  loading = false,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 font-bold uppercase tracking-widest transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed";

  const sizes = {
    sm: "px-3 py-1.5 text-[10px]",
    md: "px-5 py-2 text-[12px]",
    lg: "px-6 py-2.5 text-[13px]",
  };

  const variants = {
    primary:
      "bg-primary text-white border border-primary hover:bg-[#2c5ebc] font-black",
    secondary:
      "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
    ghost: "text-gray-600 hover:bg-gray-100 border border-transparent",
    glass:
      "bg-gray-50 border border-gray-200 text-gray-800 hover:bg-gray-100",
    danger: "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100",
  };

  return (
    <button
      className={`${baseStyles} ${sizes[size]} ${variants[variant]} ${className} ${loading ? "opacity-70 pointer-events-none" : ""}`}
      disabled={loading}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : Icon && (
        <Icon size={size === "sm" ? 14 : 16} strokeWidth={2.5} />
      )}
      {children}
    </button>
  );
};

export const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white border border-gray-200 ${className}`}
  >
    {children}
  </div>
);

export const StatCard = ({ label, value, subtext, trend }) => (
  <Card className="p-4 border-l-4 border-l-[#0F3D75]">
    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
      {label}
    </p>
    <div className="flex items-baseline gap-2">
      <div className="text-[20px] font-black text-gray-800 tabular-nums tracking-tighter">
        {value}
      </div>
      {trend && (
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 border ${trend === "up"
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-red-50 text-red-700 border-red-200"
            }`}
        >
          {trend === "up" ? "↑" : "↓"} LOG
        </span>
      )}
    </div>
    {subtext && (
      <div className="text-[11px] text-gray-400 mt-1 font-medium italic">{subtext}</div>
    )}
  </Card>
);

export const SearchInput = ({ placeholder, onChange }) => (
  <div className="relative group w-full">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <svg
        className="h-4 w-4 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
    <input
      type="text"
      className="block w-full pl-10 pr-3 py-2 border border-gray-200 bg-white placeholder-gray-400 focus:outline-none focus:border-[#0F3D75] transition-all text-[13px]"
      placeholder={placeholder}
      onChange={(e) => onChange && onChange(e.target.value)}
    />
  </div>
);

export const RegistryHeader = ({ title, subTitle, badge, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMasterSubPage =
    location.pathname.startsWith("/master") && location.pathname !== "/master";

  return (
    <div className="flex flex-col md:flex-row justify-between items-center bg-white px-8 py-3 shrink-0 w-full font-plus gap-4">
      <div className="flex items-center gap-4">
        {isMasterSubPage && (
          <button
            onClick={() => navigate("/master")}
            className="flex items-center gap-2 text-gray-400 hover:text-primary transition-all group pr-4 border-r border-gray-100"
          >
            <div className="p-2 border border-gray-200 group-hover:border-primary/30 group-hover:bg-primary/5 transition-all">
              <IoArrowBack size={18} />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[2px] hidden lg:inline">
              Back
            </span>
          </button>
        )}
        {(title || subTitle) && (
          <div className="flex flex-col">
            {title && (
              <h2 className="text-[18px] font-bold tracking-tight capitalize leading-none">
                {title}
              </h2>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
        {children}
      </div>
    </div>
  );
};

export const CalculationFooter = ({ items, className = "", children }) => (
  <div className={`sticky bottom-0 z-40 border-t border-gray-100 py-4 px-8 flex justify-center items-center gap-12 shrink-0 bg-primary/5 font-plus ${className}`}>
    <div className="flex justify-center items-center gap-12 flex-1">
      {items.map((item, idx) => (
        <React.Fragment key={idx}>
          <div className="flex flex-col items-center">
            <span className="text-[8px] text-gray-400 font-black uppercase tracking-[3px] mb-0.5 whitespace-nowrap">
              {item.label}
            </span>
            <span className={`text-[18px] font-black leading-none ${item.color || "text-slate-800"}`}>
              {item.value} {item.suffix && <span className="text-[9px] text-gray-400 ml-1 font-bold tracking-widest uppercase">{item.suffix}</span>}
            </span>
          </div>
          {idx < items.length - 1 && (
            <div className="w-[1px] bg-gray-200 h-8 self-center"></div>
          )}
        </React.Fragment>
      ))}
    </div>
    {children && (
      <div className="flex items-center gap-4">
        {children}
      </div>
    )}
  </div>
);


export const FormHeader = ({ title, onCancel }) => (
  <div className="bg-gray-200/50 px-8 py-1 flex justify-between items-center shrink-0 sticky top-0 z-50">
    <div className="flex items-center gap-4">
      <h2 className="text-[12px] font-black tracking-[3px] uppercase">
        {title}
      </h2>
    </div>
    <button
      onClick={onCancel}
      className="p-1.5 hover:bg-slate-100 transition-all rounded-sm group text-gray-400 hover:text-gray-600"
    >
      <MdClose size={20} className="group-hover:rotate-90 transition-transform duration-300" />
    </button>
  </div>
);

export const FormFooter = ({ onCancel, onSubmit, submitText = "Save", isSaving, children, className = "" }) => (
  <div className={`p-4 bg-white border-t border-gray-200 flex justify-end items-center gap-4 shrink-0 sticky bottom-0 z-50 font-plus ${className}`}>
    <button
      type="button"
      onClick={onCancel}
      className="px-10 py-2 border border-gray-300 text-gray-500 font-black text-[10px] uppercase tracking-[2.5px] hover:bg-gray-50 hover:text-gray-700 transition-all disabled:opacity-50"
      disabled={isSaving}
    >
      Cancel
    </button>
    {children}
    <Button
      variant="primary"
      onClick={onSubmit}
      loading={isSaving}
      className={`px-12 py-2 ${className}`}
    >
      {isSaving ? "Saving..." : submitText}
    </Button>
  </div>
);
